import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom'; // Added useLocation
import { collection, getDocs, query, limit, where } from 'firebase/firestore'; // Added where
import { db } from '../firebase';

export default function VisitListPage() {
    const { serverId } = useParams();
    const navigate = useNavigate();
    const location = useLocation(); // Hook to get the passed state
    
    // Check if we are in "Scenario A" (all visits) or "Scenario B" (filtered by Admin)
    const adminFilter = location.state?.filterByAdmin || null;
    const adminNameDisplay = location.state?.adminName || null;

    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); 

    useEffect(() => {
        async function fetchVisits() {
            try {
                setLoading(true);
                const visitsRef = collection(db, 'servers', serverId, 'allVisits');
                
                let q;

                if (adminFilter) {
                    // SCENARIO B: Filter by specific Admin ID/Username
                    // Note: This relies on the 'adminID' field in 'allVisits' matching the admin's username
                    q = query(
                        visitsRef, 
                        where('adminID', '==', adminFilter),
                        limit(50)
                    );
                } else {
                    // SCENARIO A: Show everything (Default)
                    q = query(visitsRef, limit(50));
                }
                
                const snapshot = await getDocs(q);
                const visitsData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Client-side sort (Newest first)
                visitsData.sort((a, b) => {
                    const dateA = a.datetime?.toDate ? a.datetime.toDate() : new Date(0);
                    const dateB = b.datetime?.toDate ? b.datetime.toDate() : new Date(0);
                    return dateB - dateA;
                });

                setVisits(visitsData);
            } catch (error) {
                console.error("Error fetching visits:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchVisits();
    }, [serverId, adminFilter]); // Re-run if filter changes

    // Helper: Determine Color based on Status
    const getStatusStyle = (status) => {
        const s = status?.toLowerCase() || '';
        if (s.includes('missed')) return { bg: '#fff5f5', color: '#c53030', border: '#feb2b2' };
        if (s.includes('completed')) return { bg: '#f0fff4', color: '#276749', border: '#9ae6b4' };
        if (s.includes('pending')) return { bg: '#ebf8ff', color: '#2b6cb0', border: '#bee3f8' };
        if (s.includes('cancelled')) return { bg: '#edf2f7', color: '#718096', border: '#cbd5e0' };
        return { bg: 'white', color: '#333', border: '#eee' };
    };

    // Client-side Status Filter (Pending, Missed, etc.)
    const filteredVisits = visits.filter(v => {
        if (filter === 'all') return true;
        return v.status?.toLowerCase() === filter;
    });

    if (loading) return <div style={{ padding: 20 }}>Loading Schedule...</div>;

    return (
        <div style={{ padding: '30px', backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button onClick={() => navigate(-1)} style={{ marginRight: '15px', padding: '8px 12px', cursor: 'pointer', border: '1px solid #ccc', background: 'white', borderRadius: '4px' }}>← Back</button>
                    <div>
                        <h1 style={{ margin: 0, color: '#2c3e50', fontSize: '24px' }}>
                            {adminFilter ? `Visits Managed by ${adminNameDisplay}` : 'All Server Visits'}
                        </h1>
                        {adminFilter && (
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                                Filtered by Admin ID: <strong>{adminFilter}</strong>
                                <button 
                                    onClick={() => navigate(location.pathname, { state: {} })} // Clear filter
                                    style={{ marginLeft: '10px', fontSize: '11px', color: '#0056b3', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                    Show All
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Filter Controls */}
                <select 
                    value={filter} 
                    onChange={(e) => setFilter(e.target.value)}
                    style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '150px' }}
                >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="missed">Missed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                </select>
            </div>

            {/* Visit Table */}
            <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #eee' }}>
                        <tr>
                            <th style={{ padding: '15px', fontSize: '12px', textTransform: 'uppercase', color: '#666' }}>Date & Time</th>
                            <th style={{ padding: '15px', fontSize: '12px', textTransform: 'uppercase', color: '#666' }}>Client (Service User)</th>
                            <th style={{ padding: '15px', fontSize: '12px', textTransform: 'uppercase', color: '#666' }}>Location</th>
                            <th style={{ padding: '15px', fontSize: '12px', textTransform: 'uppercase', color: '#666' }}>Assigned Staff</th>
                            <th style={{ padding: '15px', fontSize: '12px', textTransform: 'uppercase', color: '#666' }}>Status</th>
                            <th style={{ padding: '15px', fontSize: '12px', textTransform: 'uppercase', color: '#666' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredVisits.length > 0 ? (
                            filteredVisits.map((visit) => {
                                const style = getStatusStyle(visit.status);
                                return (
                                    <tr key={visit.id} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '15px' }}>
                                            <div style={{ fontWeight: 'bold' }}>{visit.date}</div>
                                            <div style={{ fontSize: '13px', color: '#666' }}>
                                                {visit.timeRange?.from} - {visit.timeRange?.to}
                                            </div>
                                        </td>
                                        <td style={{ padding: '15px', fontWeight: '500' }}>
                                            {visit.service_user_name || visit.username || visit.userID || 'Unknown'}
                                        </td>
                                        <td style={{ padding: '15px', fontSize: '14px', color: '#555' }}>
                                            {visit.address?.town ? `${visit.address.town}, ${visit.address.postalCode}` : 'See Details'}
                                        </td>
                                        <td style={{ padding: '15px' }}>
                                            {visit.staffIDs && visit.staffIDs.length > 0 ? (
                                                <span style={{ background: '#edf2f7', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                                                    {visit.staffIDs.length} Staff Assigned
                                                </span>
                                            ) : (
                                                <span style={{ color: '#e53e3e', fontSize: '12px' }}>Unassigned</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '15px' }}>
                                            <span style={{ 
                                                padding: '5px 10px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold', textTransform: 'capitalize',
                                                backgroundColor: style.bg, color: style.color, border: `1px solid ${style.border}`
                                            }}>
                                                {visit.status || 'Unknown'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '15px' }}>
                                            <button 
                                                onClick={() => navigate(`/servers/${serverId}/visits/${visit.id}`)}
                                                style={{ border: 'none', background: 'none', color: '#0056b3', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="6" style={{ padding: '30px', textAlign: 'center', color: '#888' }}>
                                    {adminFilter 
                                        ? `No visits found managed by ${adminFilter}.`
                                        : "No visits found matching filter."}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}